/**
 * Product Preview - DOM-based with CSS 3D transforms
 *
 * Replaces the first carousel slide content with a live preview.
 * Art Print: flat image with shadow
 * Canvas Print: CSS 3D cube (front + right + top + left + bottom faces)
 * Framed Print: DOM with clip-path mitered frame corners
 *
 * Listens for 'frame-options:change' custom events from frame-options.js.
 */
(function () {

  var configEl = document.querySelector('[data-frame-options-config]');
  if (!configEl) {
    return;
  }

  var config = JSON.parse(configEl.textContent);
  var productImageUrl = config.productImage;
  if (!productImageUrl) {
    return;
  }
  if (productImageUrl.indexOf('//') === 0) productImageUrl = 'https:' + productImageUrl;


  // --- State ---
  var previewContainer = null;
  var firstThumbImg = null;
  var isLandscape = false;
  var imageLoaded = false;
  var pendingDetail = null;
  var lastRenderedType = null;
  var lastRenderedOrientation = null;
  var lastFrameLabel = null;
  var lastMatEnabled = null;
  var lastCanvasWrap = null;

  // --- Preload product image (detect orientation) ---
  var pImg = new Image();
  pImg.crossOrigin = 'anonymous';
  pImg.onload = function () {
    isLandscape = pImg.naturalWidth > pImg.naturalHeight;
    imageLoaded = true;
    if (pendingDetail) {
      renderPreview(pendingDetail);
      pendingDetail = null;
    }
  };
  pImg.onerror = function () {
  };
  pImg.src = productImageUrl;

  // --- Helpers ---
  function getOrientedSize(printSize) {
    var w = printSize.width || 1;
    var h = printSize.height || 1;
    var big = Math.max(w, h);
    var small = Math.min(w, h);
    if (isLandscape) return { w: big, h: small };
    return { w: small, h: big };
  }

  function getPrintPixels(printSize, containerW) {
    // Use image's natural aspect ratio — never crop
    var imgW = pImg.naturalWidth || 1;
    var imgH = pImg.naturalHeight || 1;
    var imgRatio = imgW / imgH;

    // Use print size to determine overall scale
    var s = getOrientedSize(printSize);
    var maxDim = Math.max(s.w, s.h);
    // Map size to pixel range: small(8)→70%, large(40)→90% of container
    var pct = 0.70 + (maxDim - 8) * (0.20 / 32);
    pct = Math.max(0.65, Math.min(0.90, pct));
    var maxPx = containerW * pct;

    // Fit image ratio into maxPx box
    var pw, ph;
    if (imgRatio >= 1) {
      pw = maxPx;
      ph = maxPx / imgRatio;
    } else {
      ph = maxPx;
      pw = maxPx * imgRatio;
    }

    return {
      w: Math.round(pw),
      h: Math.round(ph)
    };
  }

  function fixUrl(url) {
    if (!url) return '';
    return url.indexOf('//') === 0 ? 'https:' + url : url;
  }

  // --- Setup: replace first carousel slide with our container ---
  function setupPreviewContainer() {
    if (previewContainer) return true;

    var mainSlider = document.querySelector('.js-carousel-main');
    if (!mainSlider) return false;

    // Check if Flickity should be active (multi-image products)
    var galleryEl = document.querySelector('.js-product-gallery');
    var galleryConfig = {};
    try { galleryConfig = JSON.parse(galleryEl.dataset.galleryConfig || '{}'); } catch (e) {}
    var needsFlickity = galleryConfig.mainSlider === true;

    // If Flickity is expected, wait for it to initialize before touching DOM
    if (needsFlickity) {
      var flickity = window.Flickity && Flickity.data(mainSlider);
      if (!flickity) return false;
    }

    var firstSlide = mainSlider.querySelector('.carousel-cell:not([data-mockup-handle])');
    if (!firstSlide) return false;

    var mediaContainer = firstSlide.querySelector('.product-media-container');
    if (!mediaContainer) return false;

    mediaContainer.innerHTML = '';
    // Mark this slide with the product zoom image
    firstSlide.setAttribute('data-preview-zoom', productImageUrl);

    // 1:1 aspect ratio wrapper
    var wrapper = document.createElement('div');
    wrapper.style.cssText =
      'position:relative;width:100%;padding-bottom:100%;background:#f5f5f5;';

    previewContainer = document.createElement('div');
    previewContainer.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;' +
      'display:flex;align-items:center;justify-content:center;' +
      'overflow:visible;';

    wrapper.appendChild(previewContainer);
    mediaContainer.appendChild(wrapper);

    // Thumbnail (only if Flickity carousel with thumbnails)
    if (needsFlickity) {
      var thumbWrapper = document.querySelector('.js-thumb-carousel-nav');
      if (thumbWrapper) {
        var firstThumb = thumbWrapper.querySelector('.js-thumb-item:not([data-mockup-handle])');
        if (firstThumb) {
          firstThumbImg = firstThumb.querySelector('img');
          if (firstThumbImg) {
            firstThumbImg.removeAttribute('srcset');
            firstThumbImg.removeAttribute('sizes');
            var tbr = firstThumbImg.closest('.box-ratio');
            if (tbr) tbr.style.paddingBottom = '100%';
          }
        }
      }
    }

    return true;
  }

  // =========================================================
  //  ART PRINT — flat image with drop shadow
  // =========================================================
  function renderArtPrint(detail) {
    var cw = previewContainer.offsetWidth || 600;
    var px = getPrintPixels(detail.printSize, cw);

    // Clamp to 85% of container
    var maxSide = cw * 0.85;
    if (px.w > maxSide || px.h > maxSide) {
      var scale = Math.min(maxSide / px.w, maxSide / px.h);
      px.w = Math.round(px.w * scale);
      px.h = Math.round(px.h * scale);
    }

    var img = document.createElement('img');
    img.src = productImageUrl;
    img.draggable = false;
    img.style.cssText =
      'width:' + px.w + 'px;height:' + px.h + 'px;' +
      'display:block;object-fit:cover;' +
      'box-shadow:0 6px 30px rgba(0,0,0,0.18);';

    previewContainer.appendChild(img);
  }

  // =========================================================
  //  CANVAS PRINT — full inline CSS 3D (zero external CSS dependency)
  // =========================================================
  function renderCanvasPrint(detail) {
    var cw = previewContainer.offsetWidth || 600;
    var px = getPrintPixels(detail.printSize, cw);
    var depth = 38;

    // Clamp canvas + depth to 85% of container
    var sceneW = px.w + depth * 2;
    var sceneH = px.h + depth * 2;
    var maxSide = cw * 0.85;
    if (sceneW > maxSide || sceneH > maxSide) {
      var scale = Math.min(maxSide / sceneW, maxSide / sceneH);
      px.w = Math.round(px.w * scale);
      px.h = Math.round(px.h * scale);
    }

    var perspective = Math.round(cw * 2.5);

    // Fix parent overflow that kills preserve-3d
    var el = previewContainer;
    while (el) {
      el.style.overflow = 'visible';
      if (el.classList && (el.classList.contains('carousel-cell') || el.classList.contains('js-carousel-main'))) break;
      el = el.parentElement;
    }

    // Scene — provides perspective
    var scene = document.createElement('div');
    scene.style.cssText =
      'width:' + (px.w + depth * 2) + 'px;' +
      'height:' + (px.h + depth * 2) + 'px;' +
      'display:flex;align-items:center;justify-content:center;' +
      'perspective:' + perspective + 'px;' +
      'perspective-origin:50% 45%;';

    // Cube — preserve-3d + rotation
    var cube = document.createElement('div');
    cube.style.cssText =
      'width:' + px.w + 'px;' +
      'height:' + px.h + 'px;' +
      'position:relative;' +
      'transform-style:preserve-3d;' +
      '-webkit-transform-style:preserve-3d;' +
      'transform:rotateY(-18deg) rotateX(4deg);';

    // FRONT
    var front = document.createElement('div');
    front.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;' +
      'backface-visibility:hidden;-webkit-backface-visibility:hidden;';
    var fImg = document.createElement('img');
    fImg.src = productImageUrl;
    fImg.draggable = false;
    fImg.style.cssText = 'width:100%;height:100%;display:block;object-fit:cover;';
    front.appendChild(fImg);
    cube.appendChild(front);

    // RIGHT — positioned at right edge, rotated 90° around left edge
    var right = document.createElement('div');
    right.style.cssText =
      'position:absolute;top:0;' +
      'left:' + px.w + 'px;' +
      'width:' + depth + 'px;height:100%;' +
      'transform-origin:0 0;' +
      'transform:rotateY(90deg);' +
      'backface-visibility:hidden;-webkit-backface-visibility:hidden;' +
      'overflow:hidden;' +
      'background-image:url(' + productImageUrl + ');' +
      'background-size:' + px.w + 'px 100%;' +
      'background-position:right center;' +
      'filter:brightness(0.65);';
    var rInner = document.createElement('div');
    rInner.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;' +
      'background-image:url(' + productImageUrl + ');' +
      'background-size:' + px.w + 'px 100%;' +
      'background-position:right center;' +
      'transform:scaleX(-1);' +
      'filter:brightness(0.65);';
    right.style.filter = '';
    right.appendChild(rInner);
    cube.appendChild(right);

    // TOP — above front, rotated 90° around bottom edge
    var topFace = document.createElement('div');
    topFace.style.cssText =
      'position:absolute;' +
      'top:-' + depth + 'px;left:0;' +
      'width:100%;height:' + depth + 'px;' +
      'transform-origin:center bottom;' +
      'transform:rotateX(90deg);' +
      'backface-visibility:hidden;-webkit-backface-visibility:hidden;' +
      'background:#3a3a3a;';
    cube.appendChild(topFace);

    // BOTTOM — below front, rotated -90° around top edge
    var bottomFace = document.createElement('div');
    bottomFace.style.cssText =
      'position:absolute;' +
      'bottom:-' + depth + 'px;left:0;' +
      'width:100%;height:' + depth + 'px;' +
      'transform-origin:center top;' +
      'transform:rotateX(-90deg);' +
      'backface-visibility:hidden;-webkit-backface-visibility:hidden;' +
      'background:#2a2a2a;';
    cube.appendChild(bottomFace);

    // LEFT — at left edge, rotated -90° around right edge
    var leftFace = document.createElement('div');
    leftFace.style.cssText =
      'position:absolute;top:0;' +
      'left:-' + depth + 'px;' +
      'width:' + depth + 'px;height:100%;' +
      'transform-origin:right center;' +
      'transform:rotateY(-90deg);' +
      'backface-visibility:hidden;-webkit-backface-visibility:hidden;' +
      'overflow:hidden;';
    var lInner = document.createElement('div');
    lInner.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;' +
      'background-image:url(' + productImageUrl + ');' +
      'background-size:' + px.w + 'px 100%;' +
      'background-position:left center;' +
      'filter:brightness(0.6);';
    leftFace.appendChild(lInner);
    cube.appendChild(leftFace);

    scene.appendChild(cube);
    previewContainer.appendChild(scene);
  }

  // =========================================================
  //  FRAMED PRINT — DOM + clip-path mitered corners
  // =========================================================
  function renderFramedPrint(detail) {
    var cw = previewContainer.offsetWidth || 600;
    var px = getPrintPixels(detail.printSize, cw);
    var ft = Math.round(cw * 0.04);
    var mt = detail.matEnabled ? Math.round(cw * 0.05) : 0;
    var totalW = px.w + (ft + mt) * 2;
    var totalH = px.h + (ft + mt) * 2;

    // Clamp total (image + frame + mat) to 85% of container
    var maxSide = cw * 0.85;
    if (totalW > maxSide || totalH > maxSide) {
      var scale = Math.min(maxSide / totalW, maxSide / totalH);
      totalW = Math.round(totalW * scale);
      totalH = Math.round(totalH * scale);
      px.w = Math.round(px.w * scale);
      px.h = Math.round(px.h * scale);
      ft = Math.round(ft * scale);
      mt = Math.round(mt * scale);
    }

    var assembly = document.createElement('div');
    assembly.style.cssText =
      'position:relative;' +
      'width:' + totalW + 'px;height:' + totalH + 'px;' +
      'box-shadow:0 8px 35px rgba(0,0,0,0.22);';

    var frameData = detail.frameData;

    var solidColor = '#444';
    if (frameData && frameData.label) {
      var lbl = frameData.label.toLowerCase();
      if (lbl === 'white') solidColor = '#e8e8e8';
      else if (lbl === 'black') solidColor = '#222';
      else if (lbl === 'natural') solidColor = '#c9a96e';
      else if (lbl === 'dark') solidColor = '#3a2a1a';
    }

    var sides = [
      { key: 'top',    clip: 'polygon(0 0,100% 0,calc(100% - '+ft+'px) 100%,'+ft+'px 100%)', css: 'top:0;left:0;width:100%;height:'+ft+'px;' },
      { key: 'bottom', clip: 'polygon('+ft+'px 0,calc(100% - '+ft+'px) 0,100% 100%,0 100%)', css: 'bottom:0;left:0;width:100%;height:'+ft+'px;' },
      { key: 'left',   clip: 'polygon(0 0,100% '+ft+'px,100% calc(100% - '+ft+'px),0 100%)', css: 'top:0;left:0;width:'+ft+'px;height:100%;' },
      { key: 'right',  clip: 'polygon(0 '+ft+'px,100% 0,100% 100%,0 calc(100% - '+ft+'px))', css: 'top:0;right:0;width:'+ft+'px;height:100%;' }
    ];

    sides.forEach(function (side) {
      var div = document.createElement('div');
      div.style.cssText =
        'position:absolute;' + side.css +
        'clip-path:' + side.clip + ';z-index:2;overflow:hidden;';

      var imgUrl = frameData ? frameData[side.key + '_side'] : null;
      if (imgUrl) {
        var img = document.createElement('img');
        img.src = fixUrl(imgUrl);
        img.draggable = false;
        img.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;';
        div.appendChild(img);
      } else {
        div.style.background = solidColor;
      }
      assembly.appendChild(div);
    });

    if (mt > 0) {
      var mat = document.createElement('div');
      mat.style.cssText =
        'position:absolute;z-index:1;background:#ffffff;' +
        'top:'+ft+'px;left:'+ft+'px;right:'+ft+'px;bottom:'+ft+'px;';
      assembly.appendChild(mat);
    }

    var imgEl = document.createElement('img');
    imgEl.src = productImageUrl;
    imgEl.draggable = false;
    imgEl.style.cssText =
      'position:absolute;z-index:1;display:block;object-fit:cover;' +
      'top:'+(ft+mt)+'px;left:'+(ft+mt)+'px;' +
      'width:'+px.w+'px;height:'+px.h+'px;';
    assembly.appendChild(imgEl);

    previewContainer.appendChild(assembly);
  }

  // =========================================================
  //  Thumbnail update
  // =========================================================
  function updateThumbnail() {
    if (!firstThumbImg) return;
    firstThumbImg.src = productImageUrl;
    firstThumbImg.style.objectFit = 'contain';
    firstThumbImg.style.background = '#f5f5f5';
  }

  // =========================================================
  //  Main render entry point
  // =========================================================
  function getOrientation(printSize) {
    var w = printSize.width || 1;
    var h = printSize.height || 1;
    var big = Math.max(w, h);
    var small = Math.min(w, h);
    if (big === small) return isLandscape ? 'landscape' : 'portrait';
    return isLandscape ? 'landscape' : 'portrait';
  }

  function needsRerender(detail) {
    var orientation = getOrientation(detail.printSize);
    var frameLabel = detail.frameData ? detail.frameData.label : null;
    var matOn = !!detail.matEnabled;
    var canvasWrapOn = !!detail.canvasWrap;

    if (detail.type !== lastRenderedType) return true;
    if (orientation !== lastRenderedOrientation) return true;
    if (detail.type === 'framed-print') {
      if (frameLabel !== lastFrameLabel) return true;
      if (matOn !== lastMatEnabled) return true;
    }
    if (detail.type === 'canvas-print') {
      if (canvasWrapOn !== lastCanvasWrap) return true;
    }
    return false;
  }

  var setupRetryCount = 0;

  function renderPreview(detail) {
    if (!detail) return;

    if (!setupPreviewContainer()) {
      if (setupRetryCount < 10) {
        setupRetryCount++;
        setTimeout(function () { renderPreview(detail); }, 300);
      } else {
        // Reveal gallery anyway so it doesn't stay hidden
        var gallery = document.querySelector('.frame-preview-pending');
        if (gallery) gallery.classList.add('preview-ready');
      }
      return;
    }

    if (lastRenderedType !== null && !needsRerender(detail)) {
      return;
    }

    previewContainer.innerHTML = '';

    if (detail.type === 'canvas-print' && detail.canvasWrap) {
      renderCanvasPrint(detail);
    } else if (detail.type === 'framed-print') {
      renderFramedPrint(detail);
    } else {
      renderArtPrint(detail);
    }

    lastRenderedType = detail.type;
    lastRenderedOrientation = getOrientation(detail.printSize);
    lastFrameLabel = detail.frameData ? detail.frameData.label : null;
    lastMatEnabled = !!detail.matEnabled;
    lastCanvasWrap = !!detail.canvasWrap;

    updateThumbnail();

    waitForPreviewImages(function () {
      document.dispatchEvent(new CustomEvent('product-preview:ready'));
      var mockups = (config.roomMockups || []).filter(Boolean);
      if (mockups.length === 0) {
        var gallery = document.querySelector('.frame-preview-pending');
        if (gallery) gallery.classList.add('preview-ready');
      }
    });
  }

  function waitForPreviewImages(cb) {
    if (!previewContainer) return cb();
    var imgs = previewContainer.querySelectorAll('img');
    if (imgs.length === 0) return cb();
    var remaining = imgs.length;
    function done() {
      remaining--;
      if (remaining <= 0) cb();
    }
    imgs.forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) {
        done();
      } else {
        img.onload = done;
        img.onerror = done;
      }
    });
  }

  // =========================================================
  //  Event listener
  // =========================================================
  document.addEventListener('frame-options:change', function (e) {
    if (!imageLoaded) {
      pendingDetail = e.detail;
      return;
    }
    renderPreview(e.detail);
  });

  // Check if frame-options already emitted before we loaded
  if (window._lastFrameDetail) {
    if (!imageLoaded) {
      pendingDetail = window._lastFrameDetail;
    } else {
      renderPreview(window._lastFrameDetail);
    }
  }


  // =========================================================
  //  Lightbox — click slide to zoom, prev/next navigation
  //  Preview slide shows DOM clone, others show img
  // =========================================================
  var lightbox = null;
  var lightboxContent = null;
  var lightboxCells = [];
  var lightboxIndex = 0;

  function collectCells() {
    var slider = document.querySelector('.js-carousel-main');
    if (!slider) return [];
    return Array.prototype.slice.call(slider.querySelectorAll('.carousel-cell'));
  }

  function createLightbox() {
    if (lightbox) return;

    lightbox = document.createElement('div');
    lightbox.className = 'preview-lightbox';

    var closeBtn = document.createElement('button');
    closeBtn.className = 'preview-lightbox__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '&times;';

    var prevBtn = document.createElement('button');
    prevBtn.className = 'preview-lightbox__prev';
    prevBtn.setAttribute('aria-label', 'Previous');
    prevBtn.innerHTML = '&#8249;';

    var nextBtn = document.createElement('button');
    nextBtn.className = 'preview-lightbox__next';
    nextBtn.setAttribute('aria-label', 'Next');
    nextBtn.innerHTML = '&#8250;';

    lightboxContent = document.createElement('div');
    lightboxContent.className = 'preview-lightbox__content';

    lightbox.appendChild(closeBtn);
    lightbox.appendChild(prevBtn);
    lightbox.appendChild(nextBtn);
    lightbox.appendChild(lightboxContent);
    document.body.appendChild(lightbox);

    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox) closeLightbox();
    });
    closeBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      closeLightbox();
    });
    prevBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      navigateLightbox(-1);
    });
    nextBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      navigateLightbox(1);
    });
    lightboxContent.addEventListener('click', function (e) {
      e.stopPropagation();
    });
    document.addEventListener('keydown', function (e) {
      if (!lightbox.classList.contains('is-visible')) return;
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') navigateLightbox(-1);
      else if (e.key === 'ArrowRight') navigateLightbox(1);
    });
  }

  function showSlideInLightbox(idx) {
    lightboxContent.innerHTML = '';
    var cell = lightboxCells[idx];
    if (!cell) return;

    // Preview slide (our generated DOM) — clone it
    if (cell.hasAttribute('data-preview-zoom')) {
      var srcContainer = cell.querySelector('.product-media-container');
      if (srcContainer) {
        var clone = srcContainer.cloneNode(true);
        clone.style.cssText = 'width:80vw;height:80vh;max-width:800px;max-height:800px;' +
          'display:flex;align-items:center;justify-content:center;' +
          'background:#f5f5f5;border-radius:4px;position:relative;overflow:visible;';
        // Fix the inner wrapper sizing
        var innerWrap = clone.firstElementChild;
        if (innerWrap) {
          innerWrap.style.cssText = 'position:relative;width:100%;padding-bottom:100%;';
        }
        lightboxContent.appendChild(clone);
        return;
      }
    }

    // Regular slide (mockup or product image) — show as img
    var img = cell.querySelector('.product__image') || cell.querySelector('img');
    if (img) {
      var zoomImg = document.createElement('img');
      zoomImg.className = 'preview-lightbox__img';
      zoomImg.draggable = false;
      zoomImg.src = img.getAttribute('data-zoom-src') || img.src;
      lightboxContent.appendChild(zoomImg);
    }
  }

  function navigateLightbox(dir) {
    if (lightboxCells.length <= 1) return;
    lightboxIndex = (lightboxIndex + dir + lightboxCells.length) % lightboxCells.length;
    showSlideInLightbox(lightboxIndex);
  }

  function openLightbox(slideIndex) {
    createLightbox();
    lightboxCells = collectCells();
    lightboxIndex = slideIndex;
    showSlideInLightbox(lightboxIndex);
    lightbox.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
  }

  function closeLightbox() {
    if (!lightbox) return;
    lightbox.classList.remove('is-visible');
    document.body.style.overflow = '';
    if (lightboxContent) lightboxContent.innerHTML = '';
  }

  // Delegate click on carousel slides
  document.addEventListener('click', function (e) {
    var slider = document.querySelector('.js-carousel-main');
    if (!slider || !slider.contains(e.target)) return;

    var slide = e.target.closest('.carousel-cell');
    if (!slide) return;

    var allSlides = slider.querySelectorAll('.carousel-cell');
    var idx = Array.prototype.indexOf.call(allSlides, slide);
    if (idx < 0) idx = 0;

    e.preventDefault();
    e.stopPropagation();
    openLightbox(idx);
  });
})();
